/**
 * Alert Component
 * Displays error, warning, success, or info messages
 */

import React from 'react';

import './alert.scss';

export type AlertType = 'error' | 'warning' | 'success' | 'info';

export interface AlertProps {
    type: AlertType;
    message: string;
    onDismiss?: () => void;
}

const alertIcons: Record<AlertType, React.ReactNode> = {
    error: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    warning: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    success: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    info: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
};

export const Alert: React.FC<AlertProps> = React.memo(({ type, message, onDismiss }) => {
    return (
        <div className={`alert alert--${type}`} role="alert">
            <span className="alert__icon">{alertIcons[type]}</span>
            <p className="alert__message">{message}</p>
            {onDismiss && (
                <button type="button" className="alert__dismiss" onClick={onDismiss} aria-label="Dismiss">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            )}
        </div>
    );
});

Alert.displayName = 'Alert';

export default Alert;
